import { getUtcNowUnixTimestamp, MINUTE_IN_MILLISECONDS } from "../framework/utils/date-time";
import { AuthenticationApiClient } from "./auth-client";
import { IPresenterTokenClaims, PresenterTokenClaims } from "../entities-generated/models/presenter-account/presenter-token-claims";
import { IRefreshTokenResponse } from "../entities-generated/models/authentication/refresh-token-response";
import { isThisLocalhost } from "../framework/utils";
import { IPresenterAccountPreferences } from "../entities-generated/common/presenter-account-preferences";
import { PresenterAccountStatus } from "../entities-generated/common/presenter-account-status";

const LOGIN_URL = `https://${window.location.hostname.replace(/^app\./i, '')}/login`;

class PresenterAccountServiceClass {
  public initialize() {
    if(this.serviceInitialized) { return Promise.resolve(''); }

    const promise = this.initAuthTokens();
    this.serviceInitialized = true;
    return promise;
  }

  public myAccount() {
    return this.getBearerToken()
      .then(() => this._myAccount!);
  }

  public myPreferences() {
    return this._myPreferences;
  }

  public involuntaryLogOut() {
    this.clearBearerToken();
    window.location.replace(LOGIN_URL);
  }

  public logOutThisDevice() {
    AuthenticationApiClient.signOutRequestingDevice()
      .finally(() => this.involuntaryLogOut());
  }

  public logOutAllDevices() {
    AuthenticationApiClient.signOutAllDevices()
      .finally(() => this.involuntaryLogOut());
  }

  public bearerToken(token?: string) {
    if(token === undefined) {
      return this.getBearerToken();
    }

    return this.setBearerToken(token);
  }
  public getBearerToken() {
    const now = getUtcNowUnixTimestamp();
    // for safety, consider a token that will expire within the next 5 minutes as having already expired
    if(this._bearerToken !== undefined && now < ((this.bearerTokenExpiration ?? 0) - (MINUTE_IN_MILLISECONDS * 5))) {
      return Promise.resolve(this._bearerToken);
    }

    return this.handleTokenRefreshResponse(AuthenticationApiClient.refreshApiTokens());
  }
  private setBearerTokenClaims() {
    if(this._bearerToken === undefined) { return; }
    const json = atob(this._bearerToken.split('.')[1]);
    const map = JSON.parse(json);
    const claims = new PresenterTokenClaims();
    claims.id = map['id'];
    claims.yearMonthCreated = map['id'];
    claims.displayName = map['displayName'];
    claims.fullName = map['fullName'];
    claims.email = map['email'];
    claims.accountStatus = parseInt(PresenterAccountStatus[map['accountStatus']], 10);
    claims.accountExpirationDate = parseInt(map['accountExpirationDate'], 10);
    claims.tokenExpiration = parseInt(map['tokenExpiration'], 10);
    this._myAccount = claims;

    this.bearerTokenExpiration = claims.tokenExpiration;
  }
  private setBearerToken(token: string) {
    this._bearerToken = token;
    this.setBearerTokenClaims();

    return Promise.resolve(token);
  }
  private clearBearerToken() {
    this._bearerToken = undefined;
    this._myAccount = undefined;
  }

  public handleTokenRefreshResponse(promise: Promise<IRefreshTokenResponse>) {
    return promise
    .then(resp => {
      this.setBearerToken(resp.bearerToken);
      if(this._myPreferences === undefined) {
        this._myPreferences = resp.accountPreferences;
      }
      return resp.bearerToken;
    })
    .catch(errStatus => {
      // note that a response from the development token acquisition endpoint will never reply with 401
      if(errStatus === 401) {
        this.involuntaryLogOut();

        // the idea here is that there is no way this promise will resolve before the app redirects to the login page.
        // In this way, no error view flashes on screen before the app gets a chance to complete the redirect
        return new Promise<string>((resolve) => {
          window.setTimeout(() => resolve(''), 600000);
        });
      }

      return Promise.reject(errStatus);
    });
  }

  private initAuthTokens() {
    if(isThisLocalhost()) {
      return this.handleTokenRefreshResponse(AuthenticationApiClient.getDevelopmentTokens());
    } else {
      return this.handleTokenRefreshResponse(AuthenticationApiClient.refreshApiTokens());
    }
  }

  private _bearerToken: string | undefined = undefined;
  private bearerTokenExpiration: number | undefined = undefined;
  private _myAccount: IPresenterTokenClaims | undefined = undefined;
  private _myPreferences: IPresenterAccountPreferences | undefined = undefined;
  private serviceInitialized: boolean = false;
}

export const PresenterAccountService = new PresenterAccountServiceClass();